<template>
  <form>
    <FormHeader v-if="title" :title="title" @cancel="onCancel" @submit="onSubmit"/>
    <div class="space-y-12 mt-5">
      <div class="border-b border-gray-900/10 pb-12">
        <!-- <h3 class="text-base/7 font-semibold text-gray-900">{{ $gettext("Détails de la propriété") }}</h3> -->
        <FormAddressAutocompleteField @update:address="handleAddressUpdate" v-model="addressStreet" field-path="attributes.addressStreet" :field-label="$gettext('Rue et numéro')" :field-attrs="addressStreetAttrs" :errors="errors" />
        <FormTextField v-if="unitIdentifierRequired" v-model="addressApartmentNumber" field-path="attributes.addressApartmentNumber" :field-label="unitIdentifierLabel" :field-attrs="addressApartmentNumberAttrs" :errors="errors" />
        <FormTextField v-else v-model="addressApartmentNumber" field-path="attributes.addressApartmentNumber" :field-label="$gettext('Numéro d\'appartement (optionnel)')" :field-attrs="addressApartmentNumberAttrs" :errors="errors" />
        <FormTextField v-model="addressZipcode" field-path="attributes.addressZipcode" :field-label="$gettext('Code postal')" :field-attrs="addressZipcodeAttrs" :errors="errors" />
        <FormTextField v-model="addressCity" field-path="attributes.addressCity" :field-label="$gettext('Ville')" :field-attrs="addressCityAttrs" :errors="errors" />
        <FormSelectField v-model="addressCountryId" field-path="relationships.addressCountry.data.id" :field-label="$gettext('Pays')" :field-attrs="addressCountryIdAttrs" :errors="errors" :options="countryOptions" />
        <FormSelectField v-model="propertyTypeId" field-path="relationships.propertyType.data.id" :field-label="$gettext('Type de bien')" :field-attrs="propertyTypeIdAttrs" :errors="errors" :options="propertyTypeOptions" />
        <FormTextField v-model="surfaceArea" field-path="attributes.surfaceArea" :field-label="$gettext('Superficie (m²)')" :field-attrs="surfaceAreaAttrs" :errors="errors" />
      </div>
    </div>
    <FormFooter @cancel="onCancel" @submit="onSubmit"/>
  </form>
</template>

<script setup>
import { useForm } from "vee-validate";
import * as yup from "yup";
import FormTextField from "@/components/form-fields/FormTextField.vue";
import FormSelectField from "@/components/form-fields/FormSelectField.vue";

import { computed, watch } from "vue";
import FormHeader from "@/components/form-fields/FormHeader.vue";
import FormFooter from "@/components/form-fields/FormFooter.vue";
import { useGettext } from "vue3-gettext";
import FormAddressAutocompleteField from "@/components/form-fields/FormAddressAutocompleteField.vue";
import { useCountryStore } from "@/stores/country.js";
import { usePropertyTypeStore } from "@/stores/propertyType";

const { $gettext } = useGettext();
const countryStore = useCountryStore();
const propertyTypeStore = usePropertyTypeStore();

const props = defineProps(["property", "title"]);
const emits = defineEmits(["submit", "cancel"]);

const countryOptions = computed(() =>
  countryStore.sortedCountries.map((country) => ({
    value: country.id,
    label: country.attributes.name
  }))
);

const propertyTypeOptions = computed(() =>
  propertyTypeStore.sortedPropertyTypes.map((propertyType) => ({
    value: propertyType.id,
    label: propertyType.attributes.name
  }))
);

const validationSchema = yup.lazy((values) => {
  const typeId = values?.relationships?.propertyType?.data?.id;
  const requiresUnit = propertyTypeStore.all.find((t) => t.id === typeId)
    ?.attributes?.requiresUnitIdentifier === true;

  return yup.object({
    attributes: yup.object({
      addressStreet: yup.string().required($gettext("est obligatoire")),
      addressCity: yup.string().required($gettext("est obligatoire")),
      addressZipcode: yup.number().required($gettext("est obligatoire")),
      addressApartmentNumber: requiresUnit
        ? yup.string().required($gettext("est obligatoire pour ce type de bien"))
        : yup.string().nullable(),
      surfaceArea: yup.number().positive($gettext("doit être positif")).nullable().transform((value, originalValue) => originalValue === "" ? null : value),
    }),
    relationships: yup.object({
      addressCountry: yup.object({
        data: yup.object({
          id: yup.string().required($gettext("est obligatoire"))
        })
      }),
      propertyType: yup.object({
        data: yup.object({
          id: yup.string().nullable()
        })
      })
    })
  });
});

const { errors, handleSubmit, defineField, setValues, setFieldValue } = useForm({
  validationSchema: validationSchema,
  initialValues: props.property
});

const [addressStreet, addressStreetAttrs] = defineField("attributes.addressStreet");
const [addressCity, addressCityAttrs] = defineField("attributes.addressCity");
const [addressApartmentNumber, addressApartmentNumberAttrs] = defineField("attributes.addressApartmentNumber");
const [addressZipcode, addressZipcodeAttrs] = defineField("attributes.addressZipcode");
const [addressCountryId, addressCountryIdAttrs] = defineField("relationships.addressCountry.data.id");
const [propertyTypeId, propertyTypeIdAttrs] = defineField("relationships.propertyType.data.id");
const [surfaceArea, surfaceAreaAttrs] = defineField("attributes.surfaceArea");

const selectedPropertyType = computed(() =>
  propertyTypeStore.all.find((t) => t.id === propertyTypeId.value)
);

const unitIdentifierRequired = computed(() =>
  selectedPropertyType.value?.attributes?.requiresUnitIdentifier === true
);

const unitIdentifierLabel = computed(() => {
  const typeName = selectedPropertyType.value?.attributes?.name;
  if (typeName === "Garage") return $gettext("Numéro de garage");
  if (typeName === "Parking") return $gettext("Numéro de parking");
  if (typeName === "Cave") return $gettext("Numéro de cave");
  if (typeName === "Bureau") return $gettext("Numéro de bureau");
  return $gettext("Numéro d'appartement");
});

const onFormSubmit = ((values, actions) => {
const payload = {
    ...values
  };
  emits("submit", payload, actions);
});

const onSubmit = (() => {
  handleSubmit(onFormSubmit)();
});

const onCancel = (() => {
  emits("cancel");
});

const handleAddressUpdate = (address) => {
  const components = address?.address_components || [];

  const streetNumber = components.find(c => c.types.includes("street_number"))?.long_name || "";
  const streetName = components.find(c => c.types.includes("route"))?.long_name || "";
  const city = components.find(c => c.types.includes("locality"))?.long_name || "";
  const postalCode = components.find(c => c.types.includes("postal_code"))?.long_name || "";
  const countryCode = components.find(c => c.types.includes("country"))?.short_name || "";

  setFieldValue("attributes.addressStreet", `${streetName} ${streetNumber}`.trim());
  setFieldValue("attributes.addressCity", city);
  setFieldValue("attributes.addressZipcode", postalCode);
  const matchedCountry = countryStore.sortedCountries.find(
    (country) => country.attributes.code === countryCode
  );
  if (matchedCountry) {
    setFieldValue("relationships.addressCountry.data.id", matchedCountry.id);
  } else {
    setFieldValue("relationships.addressCountry.data.id", null); // Or handle unknown country
  }
};

watch(() => props.property, (newProperty) => {
  setValues(newProperty);
});

</script>
